import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from '../components/ui/sheet';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { GooglePlacesAutocomplete } from '../components/ui/google-places-autocomplete';
import { ConfirmationDialog } from '../components/ui/confirmation-dialog';
import { 
  Plus, Pencil, Trash2, Building2, Search, Phone, Mail, User, MoreHorizontal, 
  DollarSign, Users, Star, Ban, ChevronDown, ChevronUp, MapPin, Shield, ExternalLink, Link2, UserCheck, UserPlus 
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const AUSTRALIAN_STATES = ['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT'];

const Customers = () => {
  const { api, user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'customers');
  
  // Customer state
  const [customers, setCustomers] = useState([]);
  const [priceLists, setPriceLists] = useState([]);
  const [operators, setOperators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [xeroExporting, setXeroExporting] = useState(null); // Track which customer is being exported
  const [myobExporting, setMyobExporting] = useState(null); // Track which customer is being exported to MYOB
  
  // Tenant accounting connection status
  const [accountingConnection, setAccountingConnection] = useState({
    xero_connected: false,
    myob_connected: false
  });
  
  const [customerForm, setCustomerForm] = useState({
    customer_type: 'organisation',
    company_name: '',
    contact_name: '',
    phone: '',
    email: '',
    price_list_id: '',
    notes: '',
    requires_po: false
  });

  // Contacts state
  const [contacts, setContacts] = useState([]);
  const [editingContact, setEditingContact] = useState(null);
  const [contactForm, setContactForm] = useState({
    customer_id: '',
    name: '',
    phone: '',
    email: '',
    position: '',
    is_primary: false
  });
  
  // Customer drawer tab state
  const [customerDrawerTab, setCustomerDrawerTab] = useState('details');
  const [drawerKey, setDrawerKey] = useState(0);

  // Sites state
  const [sites, setSites] = useState([]);
  const [complianceTypes, setComplianceTypes] = useState([]);
  const [siteSearchTerm, setSiteSearchTerm] = useState('');
  const [siteDialogOpen, setSiteDialogOpen] = useState(false);
  const [editingSite, setEditingSite] = useState(null);
  const [siteForm, setSiteForm] = useState({
    name: '',
    street_address: '',
    suburb: '',
    postcode: '',
    state: '',
    address: '',
    customer_id: '',
    notes: '',
    required_compliance_type_ids: [],
    preferred_compliance_type_ids: []
  });

  // Site contacts state
  const [siteContacts, setSiteContacts] = useState([]);
  const [availableCustomerContacts, setAvailableCustomerContacts] = useState([]);
  const [siteSheetTab, setSiteSheetTab] = useState('details');
  const [siteContactDialogOpen, setSiteContactDialogOpen] = useState(false);
  const [linkContactDialogOpen, setLinkContactDialogOpen] = useState(false);
  const [editingSiteContact, setEditingSiteContact] = useState(null);
  const [siteContactForm, setSiteContactForm] = useState({
    name: '',
    job_title: '',
    phone: '',
    email: '',
    is_primary: false,
    notes: ''
  });

  // Operator Preferences state
  const [preferencesDialogOpen, setPreferencesDialogOpen] = useState(false);
  const [selectedCustomerForPrefs, setSelectedCustomerForPrefs] = useState(null);
  const [customerPreferences, setCustomerPreferences] = useState([]);
  const [prefForm, setPrefForm] = useState({
    operator_id: '',
    preference_type: 'preferred',
    priority: 1,
    notes: ''
  });
  const [editingPreference, setEditingPreference] = useState(null);
  const [expandedCustomerId, setExpandedCustomerId] = useState(null);

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteType, setDeleteType] = useState(null); // 'customer', 'contact', 'site', 'site_contact', 'operator_pref'
  const [isDeleting, setIsDeleting] = useState(false);

  // Fetch contacts for a customer
  const fetchContacts = useCallback(async (customerId) => {
    try {
      const response = await api.get(`/customers/${customerId}/contacts`);
      setContacts(response.data);
    } catch (error) {
      console.error('Failed to fetch contacts:', error);
    }
  }, [api]);

  // Update URL when tab changes
  const handleTabChange = (value) => {
    setActiveTab(value);
    setSearchParams({ tab: value });
  };

  // Fetch all data
  const fetchData = useCallback(async () => {
    try {
      const [custRes, priceRes, opRes, sitesRes, compRes] = await Promise.all([
        api.get('/customers'),
        api.get('/price-lists/active'),
        api.get('/operators'),
        api.get('/sites'),
        api.get('/compliance-types')
      ]);
      setCustomers(custRes.data);
      setPriceLists(priceRes.data);
      setOperators(opRes.data);
      setSites(sitesRes.data);
      setComplianceTypes(compRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, [api]);

  // Fetch tenant accounting connection status
  const fetchAccountingConnection = useCallback(async () => {
    if (!user?.client_id) return;
    try {
      const response = await api.get(`/tenants/${user.client_id}`);
      setAccountingConnection({
        xero_connected: response.data.xero_connected || false,
        myob_connected: response.data.myob_connected || false
      });
    } catch (error) {
      console.error('Failed to fetch accounting connection status');
    }
  }, [api, user?.client_id]);

  useEffect(() => {
    fetchData();
    fetchAccountingConnection();
  }, [fetchData, fetchAccountingConnection]);

  // ==================== CUSTOMER HANDLERS ====================
  const resetCustomerForm = () => {
    setCustomerForm({
      customer_type: 'organisation',
      company_name: '',
      contact_name: '',
      phone: '',
      email: '',
      price_list_id: '',
      notes: '',
      requires_po: false
    });
    setEditingCustomer(null);
  };

  const handleOpenCustomerDialog = (customer = null, tab = 'details') => {
    if (customer) {
      setEditingCustomer(customer);
      setCustomerForm({
        customer_type: customer.customer_type || 'organisation',
        company_name: customer.company_name || '',
        contact_name: customer.contact_name,
        phone: customer.phone || '',
        email: customer.email || '',
        price_list_id: customer.price_list_id || '',
        notes: customer.notes || '',
        requires_po: customer.requires_po || false
      });
      // Fetch contacts for this customer
      fetchContacts(customer.id);
      // Reset contact form
      setEditingContact(null);
      setContactForm({
        customer_id: customer.id,
        name: '',
        phone: '',
        email: '',
        position: '',
        is_primary: false
      });
    } else {
      resetCustomerForm();
      setContacts([]);
    }
    // Set tab and increment key to force Tabs remount
    setCustomerDrawerTab(tab);
    setDrawerKey(k => k + 1);
    setDialogOpen(true);
  };

  const handleCustomerSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = { ...customerForm };
      if (!submitData.price_list_id) submitData.price_list_id = null;
      
      if (editingCustomer) {
        await api.put(`/customers/${editingCustomer.id}`, submitData);
        toast.success('Customer updated');
      } else {
        await api.post('/customers', submitData);
        toast.success('Customer added');
      }
      setDialogOpen(false);
      resetCustomerForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  // Contact handlers
  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (!editingCustomer) return;
    
    try {
      if (editingContact) {
        await api.put(`/contacts/${editingContact.id}`, contactForm);
        toast.success('Contact updated');
      } else {
        await api.post(`/customers/${editingCustomer.id}/contacts`, contactForm);
        toast.success('Contact added');
      }
      setEditingContact(null);
      setContactForm({
        customer_id: editingCustomer.id,
        name: '',
        phone: '',
        email: '',
        position: '',
        is_primary: false
      });
      fetchContacts(editingCustomer.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDeleteContact = async (contactId) => {
    const contact = contacts.find(c => c.id === contactId);
    setItemToDelete({ ...contact, customerId: editingCustomer?.id });
    setDeleteType('contact');
    setDeleteDialogOpen(true);
  };

  const handleDeleteCustomer = async (id) => {
    const customer = customers.find(c => c.id === id);
    setItemToDelete(customer);
    setDeleteType('customer');
    setDeleteDialogOpen(true);
  };

  // Unified delete confirmation handler
  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;
    setIsDeleting(true);
    try {
      switch (deleteType) {
        case 'customer':
          await api.delete(`/customers/${itemToDelete.id}`);
          toast.success('Customer deleted');
          fetchData();
          break;
        case 'contact':
          await api.delete(`/contacts/${itemToDelete.id}`);
          toast.success('Contact deleted');
          if (itemToDelete.customerId) {
            fetchContacts(itemToDelete.customerId);
          }
          break;
        case 'site':
          await api.delete(`/sites/${itemToDelete.id}`);
          toast.success('Site deleted');
          fetchData();
          break;
        case 'site_contact':
          await api.delete(`/site-contacts/${itemToDelete.id}`);
          toast.success('Contact removed from site');
          if (itemToDelete.siteId) {
            await fetchSiteContacts(itemToDelete.siteId);
            await fetchAvailableCustomerContacts(itemToDelete.customerId, itemToDelete.siteId);
          }
          break;
        case 'operator_pref':
          await api.delete(`/customers/${itemToDelete.customerId}/operator-preferences/${itemToDelete.id}`);
          toast.success('Preference removed');
          await fetchCustomerPreferences(itemToDelete.customerId);
          break;
        default:
          break;
      }
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Delete failed');
    } finally {
      setIsDeleting(false);
    }
  };

  // Get delete dialog info based on delete type
  const getDeleteDialogInfo = () => {
    switch (deleteType) {
      case 'customer':
        return {
          title: 'Delete Customer',
          description: `Are you sure you want to delete "${itemToDelete?.company_name}"? This will also delete all associated sites and contacts.`
        };
      case 'contact':
        return {
          title: 'Delete Contact',
          description: `Are you sure you want to delete "${itemToDelete?.name}"? This action cannot be undone.`
        };
      case 'site':
        return {
          title: 'Delete Site',
          description: `Are you sure you want to delete "${itemToDelete?.name}"? This will remove all site contacts as well.`
        };
      case 'site_contact':
        return {
          title: 'Remove Site Contact',
          description: `Remove "${itemToDelete?.name}" from this site?`
        };
      case 'operator_pref':
        return {
          title: 'Remove Operator Preference',
          description: `Remove this operator preference?`
        };
      default:
        return { title: 'Delete', description: 'Are you sure?' };
    }
  };

  // Send customer to Xero
  const handleSendToXero = async (customer) => {
    try {
      setXeroExporting(customer.id);
      const response = await api.post(`/xero/export-customer/${customer.id}`);
      if (response.data.status === 'linked') {
        toast.success('Customer linked to existing Xero contact');
      } else {
        toast.success('Customer exported to Xero');
      }
      fetchData(); // Refresh to show Xero badge
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send to Xero');
    } finally {
      setXeroExporting(null);
    }
  };

  const handleSendToMyob = async (customer) => {
    try {
      setMyobExporting(customer.id);
      const response = await api.post(`/myob/export-customer/${customer.id}`);
      if (response.data.status === 'linked') {
        toast.success('Customer linked to existing MYOB contact');
      } else {
        toast.success('Customer exported to MYOB');
      }
      fetchData(); // Refresh to show MYOB badge
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send to MYOB');
    } finally {
      setMyobExporting(null);
    }
  };

  const getPriceListName = (id) => {
    const pl = priceLists.find(p => p.id === id);
    return pl?.name || '-';
  };

  // ==================== SITE HANDLERS ====================
  const resetSiteForm = () => {
    setSiteForm({
      name: '',
      street_address: '',
      suburb: '',
      postcode: '',
      state: '',
      address: '',
      customer_id: '',
      notes: '',
      required_compliance_type_ids: [],
      preferred_compliance_type_ids: []
    });
    setEditingSite(null);
  };

  const handleOpenSiteDialog = async (site = null) => {
    if (site) {
      setEditingSite(site);
      setSiteForm({
        name: site.name,
        street_address: site.street_address || '',
        suburb: site.suburb || '',
        postcode: site.postcode || '',
        state: site.state || '',
        address: site.address || '',
        customer_id: site.customer_id,
        notes: site.notes || '',
        required_compliance_type_ids: site.required_compliance_type_ids || [],
        preferred_compliance_type_ids: site.preferred_compliance_type_ids || []
      });
      setSiteSheetTab('details');
      // Fetch site contacts
      await fetchSiteContacts(site.id);
      await fetchAvailableCustomerContacts(site.customer_id, site.id);
    } else {
      resetSiteForm();
      setSiteContacts([]);
      setAvailableCustomerContacts([]);
      setSiteSheetTab('details');
    }
    setSiteDialogOpen(true);
  };

  const toggleComplianceType = (typeId, listType) => {
    const currentList = siteForm[listType];
    const otherList = listType === 'required_compliance_type_ids' 
      ? 'preferred_compliance_type_ids' 
      : 'required_compliance_type_ids';
    
    if (currentList.includes(typeId)) {
      setSiteForm({ ...siteForm, [listType]: currentList.filter(id => id !== typeId) });
    } else {
      setSiteForm({ 
        ...siteForm, 
        [listType]: [...currentList, typeId],
        [otherList]: siteForm[otherList].filter(id => id !== typeId)
      });
    }
  };

  const handleSiteSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSite) {
        await api.put(`/sites/${editingSite.id}`, siteForm);
        toast.success('Site updated');
      } else {
        await api.post('/sites', siteForm);
        toast.success('Site added');
      }
      setSiteDialogOpen(false);
      resetSiteForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDeleteSite = async (id) => {
    const site = sites.find(s => s.id === id);
    setItemToDelete(site);
    setDeleteType('site');
    setDeleteDialogOpen(true);
  };

  const getCustomerName = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    return customer?.company_name || 'Unknown';
  };

  const getComplianceTypeName = (typeId) => {
    const ct = complianceTypes.find(c => c.id === typeId);
    return ct?.name || 'Unknown';
  };

  const getSiteCountForCustomer = (customerId) => {
    return sites.filter(s => s.customer_id === customerId).length;
  };

  // ==================== SITE CONTACTS HANDLERS ====================
  const fetchSiteContacts = useCallback(async (siteId) => {
    try {
      const response = await api.get(`/sites/${siteId}/contacts`);
      setSiteContacts(response.data);
    } catch (error) {
      console.error('Failed to fetch site contacts:', error);
      setSiteContacts([]);
    }
  }, [api]);

  const fetchAvailableCustomerContacts = useCallback(async (customerId, siteId) => {
    try {
      const response = await api.get(`/customers/${customerId}/contacts/available-for-site/${siteId}`);
      setAvailableCustomerContacts(response.data);
    } catch (error) {
      console.error('Failed to fetch available contacts:', error);
      setAvailableCustomerContacts([]);
    }
  }, [api]);

  const resetSiteContactForm = () => {
    setSiteContactForm({
      name: '',
      job_title: '',
      phone: '',
      email: '',
      is_primary: false,
      notes: ''
    });
    setEditingSiteContact(null);
  };

  const handleOpenSiteContactDialog = (contact = null) => {
    if (contact) {
      setEditingSiteContact(contact);
      setSiteContactForm({
        name: contact.name,
        job_title: contact.job_title || '',
        phone: contact.phone || '',
        email: contact.email || '',
        is_primary: contact.is_primary || false,
        notes: contact.notes || ''
      });
    } else {
      resetSiteContactForm();
    }
    setSiteContactDialogOpen(true);
  };

  const handleSiteContactSubmit = async (e) => {
    e.preventDefault();
    if (!editingSite) return;

    try {
      const submitData = {
        ...siteContactForm,
        site_id: editingSite.id
      };

      if (editingSiteContact) {
        await api.put(`/site-contacts/${editingSiteContact.id}`, submitData);
        toast.success('Contact updated');
      } else {
        await api.post(`/sites/${editingSite.id}/contacts`, submitData);
        toast.success('Contact added');
      }
      setSiteContactDialogOpen(false);
      resetSiteContactForm();
      await fetchSiteContacts(editingSite.id);
      await fetchAvailableCustomerContacts(editingSite.customer_id, editingSite.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleLinkCustomerContact = async (customerContactId, isPrimary = false) => {
    if (!editingSite) return;

    try {
      await api.post(`/sites/${editingSite.id}/contacts/link`, {
        customer_contact_id: customerContactId,
        is_primary: isPrimary
      });
      toast.success('Contact linked to site');
      setLinkContactDialogOpen(false);
      await fetchSiteContacts(editingSite.id);
      await fetchAvailableCustomerContacts(editingSite.customer_id, editingSite.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to link contact');
    }
  };

  const handleDeleteSiteContact = async (contactId) => {
    const contact = siteContacts.find(c => c.id === contactId);
    setItemToDelete({ ...contact, siteId: editingSite?.id, customerId: editingSite?.customer_id });
    setDeleteType('site_contact');
    setDeleteDialogOpen(true);
  };

  // ==================== OPERATOR PREFERENCES HANDLERS ====================
  const fetchCustomerPreferences = async (customerId) => {
    try {
      const res = await api.get(`/customers/${customerId}/operator-preferences`);
      setCustomerPreferences(res.data);
    } catch (error) {
      setCustomerPreferences([]);
    }
  };

  const handleOpenPreferencesDialog = async (customer) => {
    setSelectedCustomerForPrefs(customer);
    await fetchCustomerPreferences(customer.id);
    resetPrefForm();
    setPreferencesDialogOpen(true);
  };

  const resetPrefForm = () => {
    setPrefForm({
      operator_id: '',
      preference_type: 'preferred',
      priority: 1,
      notes: ''
    });
    setEditingPreference(null);
  };

  const handleEditPreference = (pref) => {
    setEditingPreference(pref);
    setPrefForm({
      operator_id: pref.operator_id,
      preference_type: pref.preference_type,
      priority: pref.priority || 1,
      notes: pref.notes || ''
    });
  };

  const handleSubmitPreference = async (e) => {
    e.preventDefault();
    if (!selectedCustomerForPrefs) return;

    try {
      const submitData = {
        ...prefForm,
        customer_id: selectedCustomerForPrefs.id,
        priority: prefForm.preference_type === 'preferred' ? parseInt(prefForm.priority) : null
      };

      if (editingPreference) {
        await api.put(`/customers/${selectedCustomerForPrefs.id}/operator-preferences/${editingPreference.id}`, submitData);
        toast.success('Preference updated');
      } else {
        await api.post(`/customers/${selectedCustomerForPrefs.id}/operator-preferences`, submitData);
        toast.success('Preference added');
      }
      await fetchCustomerPreferences(selectedCustomerForPrefs.id);
      resetPrefForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save preference');
    }
  };

  const handleDeletePreference = async (prefId) => {
    if (!selectedCustomerForPrefs) return;
    const pref = customerPreferences.find(p => p.id === prefId);
    setItemToDelete({ ...pref, customerId: selectedCustomerForPrefs.id });
    setDeleteType('operator_pref');
    setDeleteDialogOpen(true);
  };

  const toggleExpandCustomer = async (customerId) => {
    if (expandedCustomerId === customerId) {
      setExpandedCustomerId(null);
    } else {
      setExpandedCustomerId(customerId);
      await fetchCustomerPreferences(customerId);
    }
  };

  const getOperatorName = (operatorId) => {
    const op = operators.find(o => o.id === operatorId);
    return op?.name || 'Unknown';
  };

  // Filters
  const filteredCustomers = customers.filter(c =>
    c.company_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.contact_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredSites = sites.filter(s =>
    s.name.toLowerCase().includes(siteSearchTerm.toLowerCase()) ||
    s.address?.toLowerCase().includes(siteSearchTerm.toLowerCase()) ||
    s.suburb?.toLowerCase().includes(siteSearchTerm.toLowerCase()) ||
    getCustomerName(s.customer_id).toLowerCase().includes(siteSearchTerm.toLowerCase())
  );

  if (loading) {
    return <div className="animate-pulse h-96 bg-muted rounded" />;
  }

  return (
    <div className="space-y-6" data-testid="customers-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold">Customers & Sites</h1>
          <p className="text-muted-foreground">Manage your customers and their job sites</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <Card 
          className="card-industrial cursor-pointer hover:border-[#ffd108]/50 transition-colors"
          onClick={() => handleTabChange('customers')}
          data-testid="stat-card-customers"
        >
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#ffd108]/20 rounded-lg">
                <Building2 className="h-5 w-5 text-[#ffd108]" />
              </div>
              <div>
                <p className="text-2xl font-bold">{customers.length}</p>
                <p className="text-xs text-muted-foreground">Customers</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card 
          className="card-industrial cursor-pointer hover:border-blue-500/50 transition-colors"
          onClick={() => handleTabChange('sites')}
          data-testid="stat-card-sites"
        >
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/20 rounded-lg">
                <MapPin className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{sites.length}</p>
                <p className="text-xs text-muted-foreground">Sites</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card 
          className="card-industrial cursor-pointer hover:border-green-500/50 transition-colors"
          onClick={() => navigate('/data-management?tab=price-lists')}
          data-testid="stat-card-pricelists"
        >
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500/20 rounded-lg">
                <DollarSign className="h-5 w-5 text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{priceLists.length}</p>
                <p className="text-xs text-muted-foreground">Price Lists</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList>
          <TabsTrigger value="customers" data-testid="tab-customers">
            <Building2 className="h-4 w-4 mr-2" />
            Customers ({customers.length})
          </TabsTrigger>
          <TabsTrigger value="sites" data-testid="tab-sites">
            <MapPin className="h-4 w-4 mr-2" />
            Sites ({sites.length})
          </TabsTrigger>
        </TabsList>

        {/* Customers Tab */}
        <TabsContent value="customers" className="mt-6">
          <Card className="card-industrial">
            <CardHeader className="pb-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="relative flex-1 max-w-sm">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    data-testid="customer-search"
                    placeholder="Search customers..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9"
                  />
                </div>
                <Button className="btn-volt" onClick={() => handleOpenCustomerDialog()} data-testid="add-customer-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Customer
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="label-overline w-[30px]"></TableHead>
                      <TableHead className="label-overline">Company</TableHead>
                      <TableHead className="label-overline">Phone</TableHead>
                      <TableHead className="label-overline">Email</TableHead>
                      <TableHead className="label-overline">Contacts</TableHead>
                      <TableHead className="label-overline">Sites</TableHead>
                      <TableHead className="label-overline">Price List</TableHead>
                      <TableHead className="label-overline text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredCustomers.map((customer) => (
                      <>
                        <TableRow 
                          key={customer.id} 
                          data-testid={`customer-row-${customer.id}`}
                          className="cursor-pointer hover:bg-muted/50"
                          onClick={(e) => {
                            // Don't trigger if clicking the expand button or actions menu
                            if (e.target.closest('button')) return;
                            handleOpenCustomerDialog(customer);
                          }}
                        >
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0"
                              onClick={() => toggleExpandCustomer(customer.id)}
                            >
                              {expandedCustomerId === customer.id ? 
                                <ChevronUp className="h-4 w-4" /> : 
                                <ChevronDown className="h-4 w-4" />
                              }
                            </Button>
                          </TableCell>
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-2">
                              {customer.customer_type === 'individual' ? (
                                <User className="h-4 w-4 text-blue-500" />
                              ) : (
                                <Building2 className="h-4 w-4 text-[#ffd108]" />
                              )}
                              {customer.company_name}
                              {customer.customer_type === 'individual' && (
                                <Badge variant="outline" className="ml-1 text-[10px] px-1 py-0 h-4 border-blue-500 text-blue-500">
                                  Individual
                                </Badge>
                              )}
                              {customer.xero_contact_id && (
                                <Badge variant="outline" className="ml-1 text-[10px] px-1 py-0 h-4 border-[#13B5EA] text-[#13B5EA]">
                                  <Link2 className="h-2.5 w-2.5 mr-0.5" />
                                  Xero
                                </Badge>
                              )}
                              {customer.myob_customer_id && (
                                <Badge variant="outline" className="ml-1 text-[10px] px-1 py-0 h-4 border-[#6B21A8] text-[#6B21A8]">
                                  <Link2 className="h-2.5 w-2.5 mr-0.5" />
                                  MYOB
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            {customer.phone && (
                              <span className="flex items-center gap-1 text-sm text-muted-foreground">
                                <Phone className="h-3 w-3" />
                                {customer.phone}
                              </span>
                            )}
                          </TableCell>
                          <TableCell>
                            {customer.email && (
                              <span className="flex items-center gap-1 text-sm text-muted-foreground">
                                <Mail className="h-3 w-3" />
                                {customer.email}
                              </span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge variant="secondary">
                              <User className="h-3 w-3 mr-1" />
                              {customer.contact_count || 0}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              <Badge variant="secondary">
                                <MapPin className="h-3 w-3 mr-1" />
                                {getSiteCountForCustomer(customer.id)}
                              </Badge>
                              {customer.requires_po && (
                                <Badge variant="outline" className="border-amber-300 text-amber-700">
                                  PO Required
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            {customer.price_list_id && (
                              <Badge variant="outline">
                                <DollarSign className="h-3 w-3 mr-1" />
                                {getPriceListName(customer.price_list_id)}
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`customer-actions-${customer.id}`}>
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleOpenCustomerDialog(customer, 'details'); }}>
                                  <Pencil className="h-4 w-4 mr-2" />
                                  Edit Details
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleOpenCustomerDialog(customer, 'contacts'); }}>
                                  <User className="h-4 w-4 mr-2" />
                                  Contacts
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleOpenPreferencesDialog(customer); }}>
                                  <Users className="h-4 w-4 mr-2" />
                                  Operator Preferences
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={(e) => {
                                  e.stopPropagation();
                                  setSiteForm({ ...siteForm, customer_id: customer.id });
                                  setSiteDialogOpen(true);
                                }}>
                                  <MapPin className="h-4 w-4 mr-2" />
                                  Add Site
                                </DropdownMenuItem>
                                {accountingConnection.xero_connected && !customer.xero_contact_id && (
                                  <DropdownMenuItem 
                                    onClick={(e) => { e.stopPropagation(); handleSendToXero(customer); }}
                                    disabled={xeroExporting === customer.id}
                                  >
                                    {xeroExporting === customer.id ? (
                                      <>
                                        <span className="h-4 w-4 mr-2 animate-spin">⏳</span>
                                        Sending...
                                      </>
                                    ) : (
                                      <>
                                        <ExternalLink className="h-4 w-4 mr-2 text-[#13B5EA]" />
                                        Send to Xero
                                      </>
                                    )}
                                  </DropdownMenuItem>
                                )}
                                {accountingConnection.myob_connected && !customer.myob_customer_id && (
                                  <DropdownMenuItem 
                                    onClick={(e) => { e.stopPropagation(); handleSendToMyob(customer); }}
                                    disabled={myobExporting === customer.id}
                                  >
                                    {myobExporting === customer.id ? (
                                      <>
                                        <span className="h-4 w-4 mr-2 animate-spin">⏳</span>
                                        Sending...
                                      </>
                                    ) : (
                                      <>
                                        <ExternalLink className="h-4 w-4 mr-2 text-[#6B21A8]" />
                                        Send to MYOB
                                      </>
                                    )}
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuSeparator />
                                <DropdownMenuItem 
                                  onClick={(e) => { e.stopPropagation(); handleDeleteCustomer(customer.id); }}
                                  className="text-destructive"
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                        {/* Expanded preferences row */}
                        {expandedCustomerId === customer.id && (
                          <TableRow className="bg-muted/30">
                            <TableCell colSpan={8} className="py-4">
                              <div className="pl-8">
                                <h4 className="font-semibold text-sm mb-2 flex items-center gap-2">
                                  <Users className="h-4 w-4" />
                                  Operator Preferences
                                </h4>
                                {customerPreferences.length === 0 ? (
                                  <p className="text-sm text-muted-foreground">No operator preferences set</p>
                                ) : (
                                  <div className="flex flex-wrap gap-2">
                                    {customerPreferences.map(pref => (
                                      <Badge
                                        key={pref.id}
                                        variant={pref.preference_type === 'preferred' ? 'default' : 'destructive'}
                                        className="flex items-center gap-1"
                                      >
                                        {pref.preference_type === 'preferred' ? (
                                          <Star className="h-3 w-3" />
                                        ) : (
                                          <Ban className="h-3 w-3" />
                                        )}
                                        {getOperatorName(pref.operator_id)}
                                        {pref.preference_type === 'preferred' && pref.priority && (
                                          <span className="text-xs opacity-75">#{pref.priority}</span>
                                        )}
                                      </Badge>
                                    ))}
                                  </div>
                                )}
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="mt-2"
                                  onClick={() => handleOpenPreferencesDialog(customer)}
                                >
                                  <Pencil className="h-3 w-3 mr-1" />
                                  Manage Preferences
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </>
                    ))}
                  </TableBody>
                </Table>
                {filteredCustomers.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    No customers found
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Sites Tab */}
        <TabsContent value="sites" className="mt-6">
          <Card className="card-industrial">
            <CardHeader className="pb-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="relative flex-1 max-w-sm">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    data-testid="site-search"
                    placeholder="Search sites..."
                    value={siteSearchTerm}
                    onChange={(e) => setSiteSearchTerm(e.target.value)}
                    className="pl-9"
                  />
                </div>
                <Button className="btn-volt" onClick={() => handleOpenSiteDialog()} data-testid="add-site-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Site
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="label-overline">Site Name</TableHead>
                      <TableHead className="label-overline">Customer</TableHead>
                      <TableHead className="label-overline">Address</TableHead>
                      <TableHead className="label-overline">Compliance</TableHead>
                      <TableHead className="label-overline text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredSites.map((site) => (
                      <TableRow 
                        key={site.id} 
                        data-testid={`site-row-${site.id}`}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={(e) => {
                          // Don't trigger if clicking actions menu
                          if (e.target.closest('button')) return;
                          handleOpenSiteDialog(site);
                        }}
                      >
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            <MapPin className="h-4 w-4 text-[#ffd108]" />
                            {site.name}
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="flex items-center gap-1 text-sm">
                            <Building2 className="h-3 w-3 text-muted-foreground" />
                            {getCustomerName(site.customer_id)}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm text-muted-foreground">
                            {site.street_address && <div>{site.street_address}</div>}
                            <div>
                              {[site.suburb, site.state, site.postcode].filter(Boolean).join(', ')}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {site.required_compliance_type_ids?.map(id => (
                              <Badge key={id} variant="destructive" className="text-xs">
                                <Shield className="h-3 w-3 mr-1" />
                                {getComplianceTypeName(id)}
                              </Badge>
                            ))}
                            {site.preferred_compliance_type_ids?.map(id => (
                              <Badge key={id} variant="secondary" className="text-xs">
                                <Star className="h-3 w-3 mr-1" />
                                {getComplianceTypeName(id)}
                              </Badge>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`site-actions-${site.id}`}>
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => handleOpenSiteDialog(site)}>
                                <Pencil className="h-4 w-4 mr-2" />
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem 
                                onClick={() => handleDeleteSite(site.id)}
                                className="text-destructive"
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {filteredSites.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    No sites found
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Operator Preferences Dialog */}
      <Dialog open={preferencesDialogOpen} onOpenChange={setPreferencesDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Users className="h-5 w-5" />
              Operator Preferences - {selectedCustomerForPrefs?.company_name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Current Preferences */}
            {customerPreferences.length > 0 && (
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Current Preferences</Label>
                <div className="border rounded-lg divide-y">
                  {customerPreferences.map(pref => (
                    <div key={pref.id} className="flex items-center justify-between p-2">
                      <div className="flex items-center gap-2">
                        <Badge variant={pref.preference_type === 'preferred' ? 'default' : 'destructive'}>
                          {pref.preference_type === 'preferred' ? (
                            <Star className="h-3 w-3 mr-1" />
                          ) : (
                            <Ban className="h-3 w-3 mr-1" />
                          )}
                          {pref.preference_type}
                        </Badge>
                        <span className="font-medium">{getOperatorName(pref.operator_id)}</span>
                        {pref.preference_type === 'preferred' && pref.priority && (
                          <span className="text-xs text-muted-foreground">Priority #{pref.priority}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <Button size="sm" variant="ghost" onClick={() => handleEditPreference(pref)}>
                          <Pencil className="h-3 w-3" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDeletePreference(pref.id)}>
                          <Trash2 className="h-3 w-3 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Add/Edit Form */}
            <form onSubmit={handleSubmitPreference} className="space-y-3 border-t pt-4">
              <div className="space-y-2">
                <Label className="label-overline">
                  {editingPreference ? 'Edit Preference' : 'Add Preference'}
                </Label>
                <Select 
                  value={prefForm.operator_id} 
                  onValueChange={(v) => setPrefForm({ ...prefForm, operator_id: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select operator" />
                  </SelectTrigger>
                  <SelectContent>
                    {operators.map(op => (
                      <SelectItem key={op.id} value={op.id}>{op.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="label-overline">Type</Label>
                  <Select 
                    value={prefForm.preference_type} 
                    onValueChange={(v) => setPrefForm({ ...prefForm, preference_type: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="preferred">
                        <span className="flex items-center gap-1">
                          <Star className="h-3 w-3 text-green-500" />
                          Preferred
                        </span>
                      </SelectItem>
                      <SelectItem value="blocked">
                        <span className="flex items-center gap-1">
                          <Ban className="h-3 w-3 text-red-500" />
                          Blocked
                        </span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {prefForm.preference_type === 'preferred' && (
                  <div className="space-y-2">
                    <Label className="label-overline">Priority</Label>
                    <Select 
                      value={prefForm.priority.toString()} 
                      onValueChange={(v) => setPrefForm({ ...prefForm, priority: parseInt(v) })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[1, 2, 3, 4, 5].map(p => (
                          <SelectItem key={p} value={p.toString()}>#{p}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                {editingPreference && (
                  <Button type="button" variant="outline" onClick={resetPrefForm}>
                    Cancel
                  </Button>
                )}
                <Button type="submit" className="btn-volt" disabled={!prefForm.operator_id}>
                  {editingPreference ? 'Update' : 'Add'} Preference
                </Button>
              </div>
            </form>
          </div>
        </DialogContent>
      </Dialog>

      {/* Customer Sheet (Flyout Panel) - Combined with Contacts */}
      <Sheet open={dialogOpen} onOpenChange={setDialogOpen}>
        <SheetContent side="right" className="w-[800px] sm:max-w-[800px] overflow-y-auto">
          <SheetHeader className="pb-4 border-b">
            <SheetTitle className="font-heading text-xl">
              {editingCustomer ? editingCustomer.company_name : 'Add New Customer'}
            </SheetTitle>
          </SheetHeader>
          
          <Tabs key={drawerKey} defaultValue={customerDrawerTab} onValueChange={setCustomerDrawerTab} className="mt-4" data-testid="customer-form">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="details" className="flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                Details
              </TabsTrigger>
              <TabsTrigger value="contacts" className="flex items-center gap-2" disabled={!editingCustomer}>
                <User className="h-4 w-4" />
                Contacts {editingCustomer && contacts.length > 0 && `(${contacts.length})`}
              </TabsTrigger>
            </TabsList>
            
            {/* Details Tab */}
            <TabsContent value="details" className="mt-4">
              <form onSubmit={handleCustomerSubmit} className="space-y-4">
                {/* Customer Type Toggle */}
                <div className="space-y-2">
                  <Label className="label-overline">Customer Type *</Label>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant={customerForm.customer_type === 'organisation' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setCustomerForm({ ...customerForm, customer_type: 'organisation' })}
                      className="flex-1"
                      data-testid="customer-type-organisation"
                    >
                      <Building2 className="h-4 w-4 mr-2" />
                      Organisation
                    </Button>
                    <Button
                      type="button"
                      variant={customerForm.customer_type === 'individual' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setCustomerForm({ ...customerForm, customer_type: 'individual' })}
                      className="flex-1"
                      data-testid="customer-type-individual"
                    >
                      <User className="h-4 w-4 mr-2" />
                      Individual
                    </Button>
                  </div>
                </div>
                
                {/* Company Name - Only show for organisations */}
                {customerForm.customer_type === 'organisation' && (
                  <div className="space-y-2">
                    <Label className="label-overline">Company Name *</Label>
                    <Input
                      data-testid="customer-company-input"
                      value={customerForm.company_name}
                      onChange={(e) => setCustomerForm({ ...customerForm, company_name: e.target.value })}
                      placeholder="ABC Construction"
                      required
                    />
                  </div>
                )}
                
                {/* Name field - Only show for individuals */}
                {customerForm.customer_type === 'individual' && (
                  <div className="space-y-2">
                    <Label className="label-overline">Name *</Label>
                    <Input
                      data-testid="customer-contact-input"
                      value={customerForm.contact_name}
                      onChange={(e) => setCustomerForm({ ...customerForm, contact_name: e.target.value })}
                      placeholder="John Smith"
                      required
                    />
                  </div>
                )}
                
                {/* Business Phone & Email */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="label-overline">
                      {customerForm.customer_type === 'organisation' ? 'Business Phone' : 'Phone'}
                    </Label>
                    <Input
                      data-testid="customer-phone-input"
                      value={customerForm.phone}
                      onChange={(e) => setCustomerForm({ ...customerForm, phone: e.target.value })}
                      placeholder="+1 234 567 890"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="label-overline">
                      {customerForm.customer_type === 'organisation' ? 'Business Email' : 'Email'}
                    </Label>
                    <Input
                      data-testid="customer-email-input"
                      type="email"
                      value={customerForm.email}
                      onChange={(e) => setCustomerForm({ ...customerForm, email: e.target.value })}
                      placeholder="info@company.com"
                    />
                  </div>
                </div>
                
                {/* Primary Contact Section - Only for organisations */}
                {customerForm.customer_type === 'organisation' && editingCustomer && (
                  <div className="space-y-3 p-4 bg-muted/30 rounded-lg border">
                    <div className="flex items-center justify-between">
                      <Label className="label-overline flex items-center gap-2">
                        <UserCheck className="h-4 w-4" />
                        Primary Contact
                      </Label>
                      <Button 
                        type="button" 
                        variant="ghost" 
                        size="sm"
                        onClick={() => setCustomerDrawerTab('contacts')}
                        className="text-xs"
                      >
                        Manage Contacts
                      </Button>
                    </div>
                    {(() => {
                      const primaryContact = contacts.find(c => c.is_primary);
                      if (primaryContact) {
                        return (
                          <div className="space-y-1.5">
                            <p className="font-medium text-sm">{primaryContact.name}</p>
                            {primaryContact.position && (
                              <p className="text-xs text-muted-foreground">{primaryContact.position}</p>
                            )}
                            <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                              {primaryContact.phone && (
                                <span className="flex items-center gap-1">
                                  <Phone className="h-3 w-3" />
                                  {primaryContact.phone}
                                </span>
                              )}
                              {primaryContact.email && (
                                <span className="flex items-center gap-1">
                                  <Mail className="h-3 w-3" />
                                  {primaryContact.email}
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      } else {
                        return (
                          <p className="text-sm text-muted-foreground italic">
                            No primary contact set. Go to Contacts tab to add and set a primary contact.
                          </p>
                        );
                      }
                    })()}
                  </div>
                )}
                
                <div className="space-y-2">
                  <Label className="label-overline">Price List</Label>
                  <Select 
                    value={customerForm.price_list_id} 
                    onValueChange={(v) => setCustomerForm({ ...customerForm, price_list_id: v })}
                  >
                    <SelectTrigger data-testid="customer-pricelist-select">
                      <SelectValue placeholder="Select price list (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      {priceLists.map(pl => (
                        <SelectItem key={pl.id} value={pl.id}>{pl.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center space-x-3 p-3 bg-muted/50 rounded-lg">
                  <Checkbox
                    id="requires-po"
                    checked={customerForm.requires_po}
                    onCheckedChange={(checked) => setCustomerForm({ ...customerForm, requires_po: checked })}
                  />
                  <div className="space-y-1">
                    <Label htmlFor="requires-po" className="text-sm font-medium cursor-pointer">
                      Requires PO Number
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Jobs for this customer will require a mandatory Purchase Order number
                    </p>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="label-overline">Notes</Label>
                  <Textarea
                    data-testid="customer-notes-input"
                    value={customerForm.notes}
                    onChange={(e) => setCustomerForm({ ...customerForm, notes: e.target.value })}
                    placeholder="Additional notes..."
                    rows={3}
                  />
                </div>
                <div className="flex gap-2 pt-4 border-t mt-6">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                  <Button type="submit" className="btn-volt" data-testid="save-customer-btn">
                    {editingCustomer ? 'Update' : 'Add Customer'}
                  </Button>
                </div>
              </form>
            </TabsContent>
            
            {/* Contacts Tab */}
            <TabsContent value="contacts" className="mt-4">
              <div className="space-y-6">
                {/* Existing Contacts */}
                <div>
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    Contacts ({contacts.length})
                  </h3>
                  {contacts.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-4 text-center bg-muted/50 rounded-lg">
                      No contacts added yet
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {contacts.map(contact => (
                        <div key={contact.id} className={`flex items-center justify-between p-3 rounded-lg ${contact.is_primary ? 'bg-primary/10 border border-primary/30' : 'bg-muted/50'}`}>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{contact.name}</span>
                              {contact.is_primary && (
                                <Badge className="text-xs bg-primary text-primary-foreground">
                                  <UserCheck className="h-3 w-3 mr-1" />
                                  Primary
                                </Badge>
                              )}
                              {contact.position && (
                                <Badge variant="outline" className="text-xs">{contact.position}</Badge>
                              )}
                            </div>
                            <div className="flex gap-4 text-sm text-muted-foreground mt-1">
                              {contact.phone && (
                                <span className="flex items-center gap-1">
                                  <Phone className="h-3 w-3" />
                                  {contact.phone}
                                </span>
                              )}
                              {contact.email && (
                                <span className="flex items-center gap-1">
                                  <Mail className="h-3 w-3" />
                                  {contact.email}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-1">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => {
                                setEditingContact(contact);
                                setContactForm({
                                  customer_id: editingCustomer.id,
                                  name: contact.name,
                                  phone: contact.phone || '',
                                  email: contact.email || '',
                                  position: contact.position || '',
                                  is_primary: contact.is_primary || false
                                });
                              }}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleDeleteContact(contact.id)}
                              className="text-destructive hover:text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Add/Edit Contact Form */}
                <div className="border-t pt-4">
                  <h3 className="font-semibold mb-3">
                    {editingContact ? 'Edit Contact' : 'Add New Contact'}
                  </h3>
                  <form onSubmit={handleContactSubmit} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="label-overline">Name *</Label>
                        <Input
                          value={contactForm.name}
                          onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                          placeholder="Contact name"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="label-overline">Position</Label>
                        <Input
                          value={contactForm.position}
                          onChange={(e) => setContactForm({ ...contactForm, position: e.target.value })}
                          placeholder="e.g. Site Manager, Accounts"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="label-overline">Phone</Label>
                        <Input
                          value={contactForm.phone}
                          onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                          placeholder="+61 400 000 000"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="label-overline">Email</Label>
                        <Input
                          type="email"
                          value={contactForm.email}
                          onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                          placeholder="contact@company.com"
                        />
                      </div>
                    </div>
                    {/* Primary Contact Toggle */}
                    <div className="flex items-center space-x-3 p-3 bg-muted/50 rounded-lg">
                      <Checkbox
                        id="contact-is-primary"
                        checked={contactForm.is_primary}
                        onCheckedChange={(checked) => setContactForm({ ...contactForm, is_primary: checked })}
                        data-testid="contact-is-primary-checkbox"
                      />
                      <div className="space-y-1">
                        <Label htmlFor="contact-is-primary" className="text-sm font-medium cursor-pointer flex items-center gap-2">
                          <UserCheck className="h-4 w-4" />
                          Set as Primary Contact
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          Primary contact will be shown on customer details and used for communications
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {editingContact && (
                        <Button 
                          type="button" 
                          variant="outline"
                          onClick={() => {
                            setEditingContact(null);
                            setContactForm({
                              customer_id: editingCustomer?.id || '',
                              name: '',
                              phone: '',
                              email: '',
                              position: '',
                              is_primary: false
                            });
                          }}
                        >
                          Cancel Edit
                        </Button>
                      )}
                      <Button type="submit" className="btn-volt">
                        {editingContact ? 'Update Contact' : 'Add Contact'}
                      </Button>
                    </div>
                  </form>
                </div>
              </div>
              
              <div className="flex gap-2 pt-4 border-t mt-6">
                <Button variant="outline" onClick={() => setDialogOpen(false)}>Close</Button>
              </div>
            </TabsContent>
          </Tabs>
        </SheetContent>
      </Sheet>

      {/* Site Sheet (Flyout Panel) */}
      <Sheet open={siteDialogOpen} onOpenChange={setSiteDialogOpen}>
        <SheetContent side="right" className="w-[800px] sm:max-w-[800px] overflow-y-auto">
          <SheetHeader className="pb-4 border-b">
            <SheetTitle className="font-heading text-xl flex items-center gap-2">
              <MapPin className="h-5 w-5 text-[#ffd108]" />
              {editingSite ? editingSite.name : 'Add New Site'}
            </SheetTitle>
          </SheetHeader>
          
          {editingSite ? (
            <Tabs value={siteSheetTab} onValueChange={setSiteSheetTab} className="mt-4">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="details">Details</TabsTrigger>
                <TabsTrigger value="contacts" className="flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Contacts ({siteContacts.length})
                </TabsTrigger>
              </TabsList>

              {/* Details Tab */}
              <TabsContent value="details" className="mt-4">
                <form onSubmit={handleSiteSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label className="label-overline">Customer *</Label>
                    <Select 
                      value={siteForm.customer_id} 
                      onValueChange={(v) => setSiteForm({ ...siteForm, customer_id: v })}
                      required
                    >
                      <SelectTrigger data-testid="site-customer-select">
                        <SelectValue placeholder="Select customer" />
                      </SelectTrigger>
                      <SelectContent>
                        {customers.map(c => (
                          <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="label-overline">Search Address</Label>
                    <GooglePlacesAutocomplete
                      placeholder="Start typing an address..."
                      onPlaceSelect={(place) => {
                        setSiteForm(prev => ({
                          ...prev,
                          name: place.name || prev.name,
                          street_address: place.street_address || '',
                          suburb: place.suburb || '',
                          state: place.state || '',
                          postcode: place.postcode || ''
                        }));
                      }}
                      data-testid="site-address-autocomplete"
                    />
                    <p className="text-xs text-muted-foreground">Or enter address manually below</p>
                  </div>
                  <div className="space-y-2">
                    <Label className="label-overline">Site Name *</Label>
                    <Input
                      data-testid="site-name-input"
                      value={siteForm.name}
                      onChange={(e) => setSiteForm({ ...siteForm, name: e.target.value })}
                      placeholder="Site or business name"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="label-overline">Street Address</Label>
                    <Input
                      data-testid="site-street-input"
                      value={siteForm.street_address}
                      onChange={(e) => setSiteForm({ ...siteForm, street_address: e.target.value })}
                      placeholder="123 Main Street"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="label-overline">Suburb</Label>
                      <Input
                        data-testid="site-suburb-input"
                        value={siteForm.suburb}
                        onChange={(e) => setSiteForm({ ...siteForm, suburb: e.target.value })}
                        placeholder="Sydney"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="label-overline">Postcode</Label>
                      <Input
                        data-testid="site-postcode-input"
                        value={siteForm.postcode}
                        onChange={(e) => setSiteForm({ ...siteForm, postcode: e.target.value })}
                        placeholder="2000"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="label-overline">State</Label>
                    <Select 
                      value={siteForm.state} 
                      onValueChange={(v) => setSiteForm({ ...siteForm, state: v })}
                    >
                      <SelectTrigger data-testid="site-state-select">
                        <SelectValue placeholder="Select state" />
                      </SelectTrigger>
                      <SelectContent>
                        {AUSTRALIAN_STATES.map(state => (
                          <SelectItem key={state} value={state}>{state}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {complianceTypes.length > 0 && (
                    <>
                      <div className="space-y-2">
                        <Label className="label-overline flex items-center gap-2">
                          <Shield className="h-4 w-4 text-red-500" />
                          Required Compliance
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          Operators must have these certifications to work on this site
                        </p>
                        <div className="flex flex-wrap gap-2 p-2 border rounded-md max-h-32 overflow-y-auto">
                          {complianceTypes.filter(ct => ct.is_active).map(ct => (
                            <Badge
                              key={ct.id}
                              variant={siteForm.required_compliance_type_ids.includes(ct.id) ? 'destructive' : 'outline'}
                              className="cursor-pointer"
                              onClick={() => toggleComplianceType(ct.id, 'required_compliance_type_ids')}
                            >
                              {siteForm.required_compliance_type_ids.includes(ct.id) && (
                                <Shield className="h-3 w-3 mr-1" />
                              )}
                              {ct.name}
                            </Badge>
                          ))}
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label className="label-overline flex items-center gap-2">
                          <Shield className="h-4 w-4 text-yellow-500" />
                          Preferred Compliance
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          Prefer operators with these certifications (not mandatory)
                        </p>
                        <div className="flex flex-wrap gap-2 p-2 border rounded-md max-h-32 overflow-y-auto">
                          {complianceTypes.filter(ct => ct.is_active).map(ct => (
                            <Badge
                              key={ct.id}
                              variant={siteForm.preferred_compliance_type_ids.includes(ct.id) ? 'default' : 'outline'}
                              className="cursor-pointer"
                              onClick={() => toggleComplianceType(ct.id, 'preferred_compliance_type_ids')}
                            >
                              {siteForm.preferred_compliance_type_ids.includes(ct.id) && (
                                <Star className="h-3 w-3 mr-1" />
                              )}
                              {ct.name}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                  <div className="space-y-2">
                    <Label className="label-overline">Notes</Label>
                    <Textarea
                      data-testid="site-notes-input"
                      value={siteForm.notes}
                      onChange={(e) => setSiteForm({ ...siteForm, notes: e.target.value })}
                      placeholder="Site access instructions, contact details..."
                      rows={3}
                    />
                  </div>
                  <div className="flex gap-2 pt-4 border-t mt-6">
                    <Button type="button" variant="outline" onClick={() => setSiteDialogOpen(false)}>Cancel</Button>
                    <Button type="submit" className="btn-volt" data-testid="save-site-btn">
                      Update Site
                    </Button>
                  </div>
                </form>
              </TabsContent>

              {/* Contacts Tab */}
              <TabsContent value="contacts" className="mt-4 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    Site Contacts ({siteContacts.length})
                  </h3>
                  <div className="flex gap-2">
                    {availableCustomerContacts.length > 0 && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setLinkContactDialogOpen(true)}
                        data-testid="link-contact-btn"
                      >
                        <Link2 className="h-4 w-4 mr-1" />
                        Link Existing
                      </Button>
                    )}
                    <Button 
                      className="btn-volt" 
                      size="sm"
                      onClick={() => handleOpenSiteContactDialog()}
                      data-testid="add-site-contact-btn"
                    >
                      <UserPlus className="h-4 w-4 mr-1" />
                      Add New
                    </Button>
                  </div>
                </div>

                {siteContacts.length === 0 ? (
                  <div className="text-center py-8 bg-muted/50 rounded-lg">
                    <Users className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
                    <p className="text-sm text-muted-foreground mb-3">No contacts added to this site yet</p>
                    {availableCustomerContacts.length > 0 && (
                      <p className="text-xs text-muted-foreground">
                        You can link existing customer contacts or add new ones
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {siteContacts.map(contact => (
                      <div 
                        key={contact.id} 
                        className={`flex items-center justify-between p-3 rounded-lg ${
                          contact.is_primary ? 'bg-primary/10 border border-primary/30' : 'bg-muted/50'
                        }`}
                        data-testid={`site-contact-${contact.id}`}
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium">{contact.name}</span>
                            {contact.is_primary && (
                              <Badge className="text-xs bg-primary text-primary-foreground">
                                <UserCheck className="h-3 w-3 mr-1" />
                                Primary
                              </Badge>
                            )}
                            {contact.customer_contact_id && (
                              <Badge variant="outline" className="text-xs">
                                <Link2 className="h-3 w-3 mr-1" />
                                Linked
                              </Badge>
                            )}
                            {contact.job_title && (
                              <Badge variant="outline" className="text-xs">{contact.job_title}</Badge>
                            )}
                          </div>
                          <div className="flex gap-4 text-sm text-muted-foreground mt-1">
                            {contact.phone && (
                              <span className="flex items-center gap-1">
                                <Phone className="h-3 w-3" />
                                {contact.phone}
                              </span>
                            )}
                            {contact.email && (
                              <span className="flex items-center gap-1">
                                <Mail className="h-3 w-3" />
                                {contact.email}
                              </span>
                            )}
                          </div>
                          {contact.notes && (
                            <p className="text-xs text-muted-foreground mt-1">{contact.notes}</p>
                          )}
                        </div>
                        <div className="flex gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleOpenSiteContactDialog(contact)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleDeleteSiteContact(contact.id)}
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                <div className="flex gap-2 pt-4 border-t mt-6">
                  <Button variant="outline" onClick={() => setSiteDialogOpen(false)}>Close</Button>
                </div>
              </TabsContent>
            </Tabs>
          ) : (
            /* New Site Form (no tabs) */
            <form onSubmit={handleSiteSubmit} className="space-y-4 py-4">
              <div className="space-y-2">
                <Label className="label-overline">Customer *</Label>
                <Select 
                  value={siteForm.customer_id} 
                  onValueChange={(v) => setSiteForm({ ...siteForm, customer_id: v })}
                  required
                >
                  <SelectTrigger data-testid="site-customer-select">
                    <SelectValue placeholder="Select customer" />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="label-overline">Search Address</Label>
                <GooglePlacesAutocomplete
                  placeholder="Start typing an address..."
                  onPlaceSelect={(place) => {
                    setSiteForm(prev => ({
                      ...prev,
                      name: place.name || prev.name,
                      street_address: place.street_address || '',
                      suburb: place.suburb || '',
                      state: place.state || '',
                      postcode: place.postcode || ''
                    }));
                  }}
                  data-testid="site-address-autocomplete"
                />
                <p className="text-xs text-muted-foreground">Or enter address manually below</p>
              </div>
              <div className="space-y-2">
                <Label className="label-overline">Site Name *</Label>
                <Input
                  data-testid="site-name-input"
                  value={siteForm.name}
                  onChange={(e) => setSiteForm({ ...siteForm, name: e.target.value })}
                  placeholder="Site or business name"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label className="label-overline">Street Address</Label>
                <Input
                  data-testid="site-street-input"
                  value={siteForm.street_address}
                  onChange={(e) => setSiteForm({ ...siteForm, street_address: e.target.value })}
                  placeholder="123 Main Street"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="label-overline">Suburb</Label>
                  <Input
                    data-testid="site-suburb-input"
                    value={siteForm.suburb}
                    onChange={(e) => setSiteForm({ ...siteForm, suburb: e.target.value })}
                    placeholder="Sydney"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="label-overline">Postcode</Label>
                  <Input
                    data-testid="site-postcode-input"
                    value={siteForm.postcode}
                    onChange={(e) => setSiteForm({ ...siteForm, postcode: e.target.value })}
                    placeholder="2000"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="label-overline">State</Label>
                <Select 
                  value={siteForm.state} 
                  onValueChange={(v) => setSiteForm({ ...siteForm, state: v })}
                >
                  <SelectTrigger data-testid="site-state-select">
                    <SelectValue placeholder="Select state" />
                  </SelectTrigger>
                  <SelectContent>
                    {AUSTRALIAN_STATES.map(state => (
                      <SelectItem key={state} value={state}>{state}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="label-overline">Notes</Label>
                <Textarea
                  data-testid="site-notes-input"
                  value={siteForm.notes}
                  onChange={(e) => setSiteForm({ ...siteForm, notes: e.target.value })}
                  placeholder="Site access instructions, contact details..."
                  rows={3}
                />
              </div>
              <SheetFooter className="pt-4 border-t mt-6">
                <Button type="button" variant="outline" onClick={() => setSiteDialogOpen(false)}>Cancel</Button>
                <Button type="submit" className="btn-volt" data-testid="save-site-btn">
                  Add Site
                </Button>
              </SheetFooter>
            </form>
          )}
        </SheetContent>
      </Sheet>

      {/* Site Contact Dialog */}
      <Dialog open={siteContactDialogOpen} onOpenChange={setSiteContactDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingSiteContact ? 'Edit Site Contact' : 'Add New Site Contact'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSiteContactSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="label-overline">Name *</Label>
              <Input
                value={siteContactForm.name}
                onChange={(e) => setSiteContactForm({ ...siteContactForm, name: e.target.value })}
                placeholder="Contact name"
                required
                data-testid="site-contact-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="label-overline">Job Title / Role</Label>
              <Input
                value={siteContactForm.job_title}
                onChange={(e) => setSiteContactForm({ ...siteContactForm, job_title: e.target.value })}
                placeholder="e.g. Site Manager, Foreman"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="label-overline">Phone</Label>
                <Input
                  value={siteContactForm.phone}
                  onChange={(e) => setSiteContactForm({ ...siteContactForm, phone: e.target.value })}
                  placeholder="+61 400 000 000"
                />
              </div>
              <div className="space-y-2">
                <Label className="label-overline">Email</Label>
                <Input
                  type="email"
                  value={siteContactForm.email}
                  onChange={(e) => setSiteContactForm({ ...siteContactForm, email: e.target.value })}
                  placeholder="contact@company.com"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="label-overline">Notes</Label>
              <Textarea
                value={siteContactForm.notes}
                onChange={(e) => setSiteContactForm({ ...siteContactForm, notes: e.target.value })}
                placeholder="Any additional notes..."
                rows={2}
              />
            </div>

            <div className="flex items-center space-x-3 p-3 bg-muted/50 rounded-lg">
              <Checkbox
                id="site-contact-is-primary"
                checked={siteContactForm.is_primary}
                onCheckedChange={(checked) => setSiteContactForm({ ...siteContactForm, is_primary: checked })}
                data-testid="site-contact-is-primary-checkbox"
              />
              <div className="space-y-1">
                <Label htmlFor="site-contact-is-primary" className="text-sm font-medium cursor-pointer flex items-center gap-2">
                  <UserCheck className="h-4 w-4" />
                  Set as Primary Site Contact
                </Label>
                <p className="text-xs text-muted-foreground">
                  Primary contact will be the main point of reference for this site
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setSiteContactDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-volt" data-testid="site-contact-submit-btn">
                {editingSiteContact ? 'Update' : 'Add'} Contact
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Link Existing Customer Contact Dialog */}
      <Dialog open={linkContactDialogOpen} onOpenChange={setLinkContactDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Link2 className="h-5 w-5" />
              Link Customer Contact
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              Select a contact from {editingSite && getCustomerName(editingSite.customer_id)} to link to this site.
            </p>
            {availableCustomerContacts.length === 0 ? (
              <div className="text-center py-6 bg-muted/50 rounded-lg">
                <p className="text-sm text-muted-foreground">
                  All customer contacts are already linked to this site
                </p>
              </div>
            ) : (
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {availableCustomerContacts.map(contact => (
                  <div 
                    key={contact.id}
                    className="flex items-center justify-between p-3 bg-muted/50 rounded-lg hover:bg-muted transition-colors"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{contact.name}</span>
                        {contact.is_primary && (
                          <Badge variant="outline" className="text-xs">Primary</Badge>
                        )}
                        {contact.position && (
                          <Badge variant="outline" className="text-xs">{contact.position}</Badge>
                        )}
                      </div>
                      <div className="flex gap-3 text-sm text-muted-foreground mt-1">
                        {contact.phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="h-3 w-3" />
                            {contact.phone}
                          </span>
                        )}
                        {contact.email && (
                          <span className="flex items-center gap-1">
                            <Mail className="h-3 w-3" />
                            {contact.email}
                          </span>
                        )}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      className="btn-volt"
                      onClick={() => handleLinkCustomerContact(contact.id)}
                      data-testid={`link-customer-contact-${contact.id}`}
                    >
                      <Link2 className="h-4 w-4 mr-1" />
                      Link
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkContactDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title={getDeleteDialogInfo().title}
        description={getDeleteDialogInfo().description}
        confirmText="Delete"
        variant="destructive"
        onConfirm={handleConfirmDelete}
        isLoading={isDeleting}
        testId="delete-confirmation-dialog"
      />
    </div>
  );
};

export default Customers;
